"use client";

import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useEvent, useLiveRef, useSafeLayoutEffect } from "./ABQUS43J.js";
import { __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/utils/store.tsx
import { hasOwnProperty, identity } from "@ariakit/core/utils/misc";
import { batch, init, subscribe, sync } from "@ariakit/core/utils/store";
import * as React from "react";
import useSyncExternalStoreExports from "use-sync-external-store/shim/index.js";
var useSyncExternalStore = useSyncExternalStoreExports.useSyncExternalStore;
var noopSubscribe = function noopSubscribe() {
  return function () {};
};
function useStoreState(store) {
  var keyOrSelector = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : identity;
  var storeSubscribe = React.useCallback(function (callback) {
    if (!store) return noopSubscribe();
    return subscribe(store, null, callback);
  }, [store]);
  var getSnapshot = function getSnapshot() {
    var key = typeof keyOrSelector === "string" ? keyOrSelector : null;
    var selector = typeof keyOrSelector === "function" ? keyOrSelector : null;
    var state = store == null ? void 0 : store.getState();
    if (selector) return selector(state);
    if (!state) return;
    if (!key) return;
    if (!hasOwnProperty(state, key)) return;
    return state[key];
  };
  return useSyncExternalStore(storeSubscribe, getSnapshot, getSnapshot);
}
function useStoreStateObject(store, object) {
  var objRef = React.useRef({});
  var storeSubscribe = React.useCallback(function (callback) {
    if (!store) return noopSubscribe();
    return subscribe(store, null, callback);
  }, [store]);
  var getSnapshot = function getSnapshot() {
    var state = store == null ? void 0 : store.getState();
    var updated = false;
    var obj = objRef.current;
    for (var prop in object) {
      var keyOrSelector = object[prop];
      if (typeof keyOrSelector === "function") {
        var value = keyOrSelector(state);
        if (value !== obj[prop]) {
          obj[prop] = value;
          updated = true;
        }
      }
      if (typeof keyOrSelector === "string") {
        if (!state) continue;
        if (!hasOwnProperty(state, keyOrSelector)) continue;
        var _value = state[keyOrSelector];
        if (_value !== obj[prop]) {
          obj[prop] = _value;
          updated = true;
        }
      }
    }
    if (updated) {
      objRef.current = __spreadValues({}, obj);
    }
    return objRef.current;
  };
  return useSyncExternalStore(storeSubscribe, getSnapshot, getSnapshot);
}
function useStoreProps(store, props, key, setKey) {
  var value = hasOwnProperty(props, key) ? props[key] : void 0;
  var setValue = setKey ? props[setKey] : void 0;
  var propsRef = useLiveRef({
    value: value,
    setValue: setValue
  });
  useSafeLayoutEffect(function () {
    return sync(store, [key], function (state, prev) {
      var _propsRef$current = propsRef.current,
        value2 = _propsRef$current.value,
        setValue2 = _propsRef$current.setValue;
      if (!setValue2) return;
      if (state[key] === prev[key]) return;
      if (state[key] === value2) return;
      setValue2(state[key]);
    });
  }, [store, key]);
  useSafeLayoutEffect(function () {
    if (value === void 0) return;
    store.setState(key, value);
    return batch(store, [key], function () {
      if (value === void 0) return;
      store.setState(key, value);
    });
  });
}
function useStore(createStore, props) {
  var _React$useState = React.useState(function () {
      return createStore(props);
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    store = _React$useState2[0],
    setStore = _React$useState2[1];
  useSafeLayoutEffect(function () {
    return init(store);
  }, [store]);
  var useState2 = React.useCallback(function (keyOrSelector) {
    return useStoreState(store, keyOrSelector);
  }, [store]);
  var memoizedStore = React.useMemo(function () {
    return __spreadProps(__spreadValues({}, store), {
      useState: useState2
    });
  }, [store, useState2]);
  var updateStore = useEvent(function () {
    setStore(function (store2) {
      return createStore(__spreadValues(__spreadValues({}, props), store2.getState()));
    });
  });
  return [memoizedStore, updateStore];
}
export { useStoreState, useStoreStateObject, useStoreProps, useStore };